import qs from './normalizingQs';
import camelCase from 'voca/camel_case';
import snakeCase from 'voca/snake_case';
import createKeyTransformer from './createKeyTransformer';
import Debug from 'debug';

const debug = Debug('mnd:lib:normalizingFetch:debug');
const trace = Debug('mnd:lib:normalizingFetch:trace');
const toSnakeCase = createKeyTransformer(snakeCase);
const toCamelCase = createKeyTransformer(camelCase);

let csrfToken = null;

const fetchError = (json, response, url) => {
  const err = new Error(
    `HTTP Error ${response.status} on ${url}: ${response.statusText}`,
  );
  err.response = {
    ok: response.ok,
    status: response.status,
    redirected: response.redirected,
    url: response.url,
  };
  Object.assign(err, toCamelCase(json));
  throw err;
};

function unrecoverableError(json, response, url) {
  const err = new Error(
    `HTTP Error ${response.status} on ${url}: ${response.statusText}`,
  );

  err.response = response;
  err.errors = [
    {
      code: response.status,
      description: response.statusText,
    },
  ];

  throw err;
}

function convertQueryString(queryString) {
  const snakeCaseParams = toSnakeCase(qs.parse(queryString));
  return qs.stringify(snakeCaseParams, { arrayFormat: 'brackets' });
}

function detailedResponse(json, res) {
  const body = toCamelCase(json);
  const headers = {};
  res.headers.forEach((val, key) => (headers[key.toLowerCase()] = val));

  return {
    headers,
    body,
    status: res.status,
  };
}

export default async (url, options = {}) => {
  const headers = { Accept: 'application/json' };

  const [baseUrl, queryString] = url.split('?');
  const convertedUrl = queryString
    ? `${baseUrl}?${convertQueryString(queryString)}`
    : url;

  let body = options && options.body;
  if (body instanceof FormData) {
    headers['Content-Type'] = 'multipart/form-data';
  } else if (body) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(
      toSnakeCase(typeof body === 'string' ? JSON.parse(body) : body),
    );
  }

  // Only add headers if we send data to the server
  // Some endpoints doesn't like them and block the OPTION request and you end up with
  // a cross origin issue. i.e.: https://learn.mynewsdesk.com/en/wp-json/wp/v2/types/update
  const csrfTokenMetaTag = document.getElementsByName('csrf-token')[0];
  csrfToken = csrfTokenMetaTag && csrfTokenMetaTag.getAttribute('content');

  if (body && csrfToken) {
    headers['X-CSRF-Token'] = csrfToken;
  }

  if (body && typeof body === 'string') {
    headers['Content-Type'] = 'application/json';
  }

  if (options.method === 'DELETE' && csrfToken) {
    headers['X-CSRF-Token'] = csrfToken;
  }

  const response = await fetch(convertedUrl, {
    method: 'GET',
    credentials: 'same-origin',
    ...options,
    headers: { ...headers, ...options.headers },
    body,
  });

  trace('raw response', response);

  if (response.status === 204) {
    return options.detailedResponse
      ? detailedResponse(null, response)
      : response;
  }

  let json;
  try {
    json = await response.json();
  } catch (e) {
    debug('failed to parse json', e);
    throw new Error(
      'Invalid body: Response status was 200 but its body was empty or malformed.\n' +
        'If the body should be empty the response status should be 204.',
    );
  }

  let parsedJSON = json;
  if (response.status >= 500) {
    parsedJSON = unrecoverableError(json, response, convertedUrl);
  } else if (response.status >= 400 && response.status < 500) {
    parsedJSON = fetchError(json, response, convertedUrl);
  } else {
    parsedJSON = toCamelCase(json);
  }

  debug('parsed json', parsedJSON);

  return options.detailedResponse
    ? detailedResponse(parsedJSON, response)
    : parsedJSON;
};
